import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { 
  Shield, LayoutDashboard, ShieldAlert, ScrollText, Users, 
  LogOut, Eye, Server, Wifi, Activity, X, Clock, Terminal, 
  Settings, UserCircle, AlertTriangle, BrainCircuit, CheckCircle, ChevronDown, MessageSquare, CreditCard, Copy, Check, Trash2, Zap    
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import SubscriptionGuard from './SubscriptionGuard'; 
import logo from '../img/image-2026-05-21-08-52-40-636.png';

const StatCard = ({ label, value, color, icon }) => (
  <div style={{
    background: '#111827',
    border: `1px solid ${color}44`,
    borderRadius: '12px',
    padding: '20px',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    boxShadow: `0 4px 20px -5px ${color}15`
  }}>
    <div className="d-flex justify-content-between align-items-start">
      <span style={{ color: '#94a3b8', fontSize: '0.85rem', fontWeight: '500' }}>{label}</span>
      <div style={{ background: `${color}22`, color: color, padding: '8px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {icon}
      </div>
    </div>
    <div style={{ fontSize: '2.2rem', fontWeight: '700', color: color, marginTop: '10px', lineHeight: '1' }}>
      {value}
    </div>
  </div>
);

const Dashboard = ({ onLogout }) => {
  const [data, setData] = useState({subscription_status: 'loading', alerts: 0, agents_online: 0, uptime: '...', log_rate: '...', chart_data: [] });
  const [relatedLogs, setRelatedLogs] = useState([]); 
  const [users, setUsers] = useState([]); 
  const [selectedInc, setSelectedInc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [allIncidents, setAllIncidents] = useState([]); 
  const [recentIncidents, setRecentIncidents] = useState([]); 
  const tenantId = localStorage.getItem('tenant_id');
  const [copied, setCopied] = useState(false); 
  const navigate = useNavigate();
  const userRole = localStorage.getItem('role');
  const currentUser = localStorage.getItem('user');
  const [expandedLogId, setExpandedLogId] = useState(null);
  const [companyName, setCompanyName] = useState('');

  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isChatLoading, setIsChatLoading] = useState(false);
  const [availableChats, setAvailableChats] = useState([]);
  const [activeChatId, setActiveChatId] = useState(null);
  const [isChatView, setIsChatView] = useState('list');
  const [activeChatOwnerId, setActiveChatOwnerId] = useState(null);
  const isTokensExpired = Number(tenantId) !== 1 && data.ai_tokens_spent >= (data.ai_tokens_limit || 1000000);
  const [ipIntelligence, setIpIntelligence] = useState([]);


const getOs = (desc) => {
    if (!desc) return "Linux System";
    const match = desc.match(/SYSTEM OS:\s*(.*)/i);
    return match ? match[1].split('\n')[0].trim() : "Linux System";
  };

  const getAnalysisContent = (inc) => {
    const desc = inc.description || inc.summary;
    if (!desc) return "No technical description provided.";
    const marker = "ANALYSIS:";
    const idx = desc.toUpperCase().indexOf(marker);
    if (idx !== -1) {
      const result = desc.substring(idx + marker.length).trim();
      if (result) return result;
    }
    return desc.replace(/SYSTEM OS:.*?\n/i, "").replace(/ANALYSIS:/i, "").trim();
  };

  const handleCopy = (text) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const renderRecommendation = (rawText) => {
    if (!rawText || typeof rawText !== 'string') return null;
    let plan = [], commands = [], verification = [];
    if (rawText.includes('[SECTION]')) {
      const sections = rawText.split('[SECTION]');
      plan = sections[0]?.trim().split('\n').filter(Boolean) || [];
      commands = sections[1]?.trim().split('\n').filter(Boolean) || [];
      verification = sections[2]?.trim().split('\n').filter(Boolean) || [];
    } else { plan = [rawText]; }

    const cleanListMarkers = (text) => {
      return text.replace(/^[ \t]*(\d+[\s.)]*|[-•*][\t ]*)/, '').trim();
    };

    return (
      <>
       <div className="mb-4 text-start">
          <div className="small text-success text-uppercase fw-bold mb-2">Resolution Plan</div>
          <div className="ps-2">
            {plan.map((line, i) => (
              <div key={i} className="text-white small mb-2 d-flex gap-2 align-items-start">
                <div className="text-success mt-1" style={{fontSize: '8px', minWidth: '8px'}}>●</div>
                <div>{cleanListMarkers(line)}</div>
              </div>
            ))}
          </div>
        </div>

        {commands.length > 0 && commands[0] !== "" && (
          <div className="mb-4 text-start">
            <div className="small text-warning text-uppercase fw-bold mb-2">Commands to Execute</div>
            <div className="p-3 rounded bg-black border border-secondary shadow-lg position-relative">
              <button onClick={() => handleCopy(commands.join('\n'))} className="btn btn-xs btn-outline-warning py-0 px-2 position-absolute" style={{fontSize: '9px', right: '10px', top: '10px'}}>
                {copied ? <Check size={10}/> : <Copy size={10}/>} {copied ? 'COPIED' : 'COPY'}
              </button>
              {commands.map((cmd, i) => (
                <div key={i} className="d-flex gap-2 mb-1">
                  <span className="text-secondary opacity-50">$</span>
                  <code className="text-warning small" style={{wordBreak: 'break-all'}}>{cmd.trim()}</code>
                </div>
              ))}
            </div>
          </div>
        )}
        {verification.length > 0 && (
          <div className="mb-4 text-start">
            <div className="small text-info text-uppercase fw-bold mb-2">Verification</div>
            <div className="ps-2">
              {verification.map((line, i) => (
                <div key={i} className="d-flex align-items-start gap-2 mb-2 text-white-50 small">
                  <div className="mt-1"><CheckCircle size={12} className="text-info"/></div>
                  <div>{line.replace(/^\d+[\s.)]+/, '').trim()}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </>
    );
  };  


const refresh = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { 'X-NOC-Token': token } };

      const [s, i, u, n] = await Promise.all([
        axios.get('https://ai-noc.skynix.co/api/stats', config),
        axios.get('https://ai-noc.skynix.co/api/incidents?page=1&limit=50', config), 
        axios.get('https://ai-noc.skynix.co/api/users', config).catch(() => ({ data: [] })),
        axios.get('https://ai-noc.skynix.co/api/tenant/info', config)
      ]);
      
      setData(s.data);
      setUsers(u.data || []);
      setCompanyName(n.data.name);
      
     
      const incidentItems = i.data.items || []; 
      setAllIncidents(incidentItems); 

      const parseDate = (str) => {  };
      const sorted = [...incidentItems].sort((a, b) => parseDate(b.last_seen) - parseDate(a.last_seen));
      setRecentIncidents(sorted.slice(0, 10)); 
    } catch (e) { console.error("Refresh error:", e); }
};



  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('payment') === 'success') {
      alert(" План успішно оновлено! Ваші ліміти розширено.");
      window.history.replaceState({}, document.title, "/");
      refresh();
    }

    refresh(); 
    const t = setInterval(refresh, 15000); 
    
    return () => clearInterval(t); 
  }, []);

  useEffect(() => {
      const chatContainer = document.getElementById('chat-scroll');
      if (chatContainer) {
        chatContainer.scrollTop = chatContainer.scrollHeight;
      }
    }, [chatMessages, isChatView]);

  const inspectIncident = async (inc) => {
  console.log("Inspecting incident:", inc);  
  setSelectedInc(inc);

  setChatMessages([]);     
  setActiveChatId(null);   
  setAvailableChats([]); 
  setIsChatView('list');   
  setIsChatOpen(false);

  let intel = [];
  try {

    intel = inc.ip_intel 
      ? (typeof inc.ip_intel === 'string' ? JSON.parse(inc.ip_intel) : inc.ip_intel)
      : [];
  } catch (e) {
    console.error("Parsing ip_intel failed", e);
    intel = [];
  }
  setIpIntelligence(intel); 

  fetchIncidentChats(inc.id);

  try {
    const token = localStorage.getItem('token'); 
    const res = await axios.get(`https://ai-noc.skynix.co/api/incidents/${inc.id}/logs`, { 
      headers: { 'X-NOC-Token': token } 
    });
    setRelatedLogs(res.data); 
  } catch (e) { console.error("Logs load failed:", e); }
};


    const handleAssign = async (incId, username) => {
    try {
        const token = localStorage.getItem('token');
        await axios.post(`https://ai-noc.skynix.co/api/incidents/${incId}/assign`, 
            { assignee: username || null }, 
            { headers: { 'X-NOC-Token': token } }
        );
        refresh();
    } catch (e) { 
        console.error("Error assigning incident:", e); 
    }
};

  const handleResolve = async (incId) => {
    try {
      await axios.post(`https://ai-noc.skynix.co/api/incidents/${incId}/resolve`, {}, { headers: { 'X-NOC-Token': localStorage.getItem('token') } });
      refresh();
      setSelectedInc(null);
    } catch (e) { console.error(e); }
  };

  const fetchIncidentChats = async (incId) => {
  try {
    const res = await axios.get(`https://ai-noc.skynix.co/api/incidents/${incId}/chats`, {
      headers: { 'X-NOC-Token': localStorage.getItem('token') }
    });
    setAvailableChats(res.data);
  } catch (e) { console.error("Error fetching chats", e); }
};


const handleCreateNewChat = async () => {
  setIsChatLoading(true);
  try {
    const res = await axios.post(`https://ai-noc.skynix.co/api/incidents/${selectedInc.id}/chats/new`, 
      { title: `Investigation ${new Date().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}` },
      { headers: { 'X-NOC-Token': localStorage.getItem('token') } }
    );
    setActiveChatId(res.data.chat_id);
    setActiveChatOwnerId(Number(tenantId)); 
    setChatMessages([]);
    setIsChatView('messages');
    fetchIncidentChats(selectedInc.id);
  } catch (e) { alert("Error creating chat"); }
  setIsChatLoading(false);
};


const sendChatMessage = async () => {
  if (!chatInput.trim() || !activeChatId) return;
  
  const userMsg = { role: 'user', content: chatInput };
  setChatMessages(prev => [...prev, userMsg]);
  const currentInput = chatInput;
  setChatInput('');
  setIsChatLoading(true);

  try {
    const res = await axios.post(`https://ai-noc.skynix.co/api/chats/${activeChatId}/message`, 
      { message: currentInput },
      { headers: { 'X-NOC-Token': localStorage.getItem('token') } }
    );
    setChatMessages(res.data.history);

    if (res.data.new_title) {
        fetchIncidentChats(selectedInc.id);
    }
    
    refresh();
  } catch (e) { alert("Chat error"); }
  setIsChatLoading(false);
};

const loadChatMessages = async (chatId) => {
  setIsChatLoading(true);
  try {
    const res = await axios.get(`https://ai-noc.skynix.co/api/chats/${chatId}`, {
      headers: { 'X-NOC-Token': localStorage.getItem('token') }
    });
    setChatMessages(res.data.history || []);
    setActiveChatId(chatId);
    setIsChatView('messages');
  } catch (e) {
    alert("Error loading chat history");
  }
  setIsChatLoading(false);
};

const loadChatSession = async (chatId, ownerId) => { 
  setIsChatLoading(true);
  try {
    const token = localStorage.getItem('token');
    const res = await axios.get(`https://ai-noc.skynix.co/api/chats/${chatId}`, {
      headers: { 'X-NOC-Token': token }
    });
    
    setChatMessages(res.data.history || []);
    setActiveChatId(chatId);
    setActiveChatOwnerId(ownerId); 
    setIsChatView('messages'); 
  } catch (e) {
    alert("Error loading chat");
  }
  setIsChatLoading(false);
};

 const handleDeleteChat = async (e, chatId) => {
    e.stopPropagation(); 
    if (!window.confirm("Delete this investigation session?")) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`https://ai-noc.skynix.co/api/chats/${chatId}`, {
        headers: { 'X-NOC-Token': token }
      });
      fetchIncidentChats(selectedInc.id);
      if (activeChatId === chatId) {
        setIsChatView('list');
        setActiveChatId(null);
      }
    } catch (e) {
      alert("Error deleting chat: " + (e.response?.data?.detail || e.message));
    }
  };


  return (
    <div className="d-flex" style={{minHeight: '100vh', backgroundColor: '#0b0e14', color: '#e2e8f0'}}>
       <SubscriptionGuard data={data} onLogout={onLogout} tenantId={tenantId}>

      <div className="sidebar" style={{ width: '64px', background: '#0d1117', borderRight: '1px solid #1e293b', height: '100vh', position: 'fixed', display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '25px 0', zIndex: 1050 }}>
        <div
          className="sidebar-item d-flex justify-content-center"
          data-label="Go to Landing Page" 
          onClick={() => window.location.href = 'https://noc.skynix.co'}
          style={{
            transform: 'translateY(-6px)',
            marginBottom: '20px',
            cursor: 'pointer' 
          }}
        >
          <img
            src={logo}
            alt="SKYNIX"
            style={{
              width: '34px',
              height: '34px',
              objectFit: 'contain'
            }}
          />
        </div>
        <div className="sidebar-item active" data-label="Dashboard" onClick={() => navigate('/')} style={{ background: 'rgba(0, 242, 255, 0.1)', color: '#00f2ff', padding: '10px', borderRadius: '8px', cursor: 'pointer', marginBottom: '30px' }}><LayoutDashboard size={24} /></div>
        <div className="sidebar-item" data-label="Security Incidents" onClick={() => navigate('/incidents')} style={{ color: '#94a3b8', cursor: 'pointer', marginBottom: '30px' }}><ShieldAlert size={24} /></div>
        <div className="sidebar-item" data-label="System Logs" onClick={() => navigate('/logs')} style={{ color: '#94a3b8', cursor: 'pointer', marginBottom: '30px' }}><ScrollText size={24} /></div>
        <div className="sidebar-item" data-label="Infrastructure" onClick={() => navigate('/infrastructure')} style={{ color: '#94a3b8', cursor: 'pointer', marginBottom: '30px' }}><Server size={24} /></div>
        <div className="sidebar-item" data-label="User Management" onClick={() => navigate('/users')} style={{ color: '#94a3b8', cursor: 'pointer', marginBottom: '30px' }}><Users size={24} /></div>
        <div className="sidebar-item" data-label="Billing & Plans" onClick={() => navigate('/subscription')} style={{ color: '#94a3b8', cursor: 'pointer', marginBottom: '30px' }}>
            <CreditCard size={24} />
        </div>
        <div className="sidebar-item mt-auto mb-3" data-label="Settings" onClick={() => navigate('/settings')} style={{ color: '#94a3b8', cursor: 'pointer', marginBottom: '20px' }}><Settings size={24} /></div>
        <div className="sidebar-item mb-3" data-label="Logout" onClick={onLogout} style={{cursor: 'pointer'}}><LogOut size={24} className="text-danger" /></div>
      </div>

     <div className="main-wrapper" style={{ marginLeft: '64px', width: 'calc(100% - 64px)' }}>
             
             
             <header className="soc-header" style={{ height: '60px', borderBottom: '1px solid #1e293b', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 24px', background: '#0b0e14' }}>
               <div className="d-flex align-items-center gap-3">
                 
                <h5 
                  className="sidebar-item tooltip-bottom m-0 d-flex align-items-center"
                  data-label="Go to Landing Page"
                  onClick={() => window.location.href = 'https://noc.skynix.co'}
                  style={{ 
                    color: '#00f2ff', 
                    fontSize: '1.1rem', 
                    cursor: 'pointer',
                    lineHeight: '1',      
                    transform: 'translateY(1px)' 
                  }}
                >
                  SKYNIX <span className="text-white ms-2">AI-NOC</span>
                </h5>
                 
                 <div style={{ width: '1px', height: '24px', background: '#334155' }}></div>
                 <div className="text-secondary small d-flex align-items-center gap-2" style={{ fontSize: '0.9rem', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                   <Activity size={16} className="text-info" /> Security Operations Center
                 </div>
               </div>
               
               <div className="d-flex gap-4 align-items-center">
                <div style={{ 
                                               background: data.subscription_status === 'active' 
                                                 ? 'rgba(0, 242, 255, 0.1)' 
                                                 : data.subscription_status === 'loading' 
                                                   ? 'rgba(148, 163, 184, 0.1)' 
                                                   : 'rgba(239, 68, 68, 0.1)',  
                                               
                                               color: data.subscription_status === 'active' 
                                                 ? '#00f2ff' 
                                                 : data.subscription_status === 'loading' 
                                                   ? '#94a3b8' 
                                                   : '#ef4444', 
                                               
                                               padding: '4px 14px', 
                                               borderRadius: '20px', 
                                               fontSize: '0.8rem', 
                                               border: `1px solid ${
                                                 data.subscription_status === 'active' 
                                                   ? '#00f2ff44' 
                                                   : data.subscription_status === 'loading' 
                                                     ? '#94a3b844' 
                                                     : '#ef444444'
                                               }`, 
                                               fontWeight: '600' 
                                             }}>
                                               {data.subscription_status === 'loading' 
                                                 ? 'Verifying license...' 
                                                 : data.subscription_status === 'active'
                                                   ? `Plan: ${data.plan_name} (${data.agents_online}/${data.max_servers})`
                                                   : 'SUBSCRIPTION INACTIVE'
                                               }
                                             </div>
                                             <div className="header-metric small text-secondary">
                                           <BrainCircuit size={14} className="me-1" style={{ color: '#a855f7' }}/> AI Tokens: <b className={data.ai_tokens_spent >= data.ai_tokens_limit ? "text-danger" : "text-white"}>
                                               {data.ai_tokens_spent?.toLocaleString() || 0} / {data.ai_tokens_limit?.toLocaleString() || '1,000,000'}
                                           </b>
                                         </div>
                 <div style={{ background: 'rgba(16,185,129,0.1)', color: '#10b981', padding: '4px 14px', borderRadius: '20px', fontSize: '0.8rem', border: '1px solid #10b98144', fontWeight: '600' }}>
                    Core: Healthy
                 </div>
                 <div className="header-metric small text-secondary">
                    <Server size={14} className="me-1" style={{ color: '#00f2ff' }}/> Agents Online: <b className="text-white">{data.agents_online}</b>
                 </div>
                 <div className="header-metric small text-secondary">
                    <Wifi size={14} className="me-1" style={{ color: '#00f2ff' }}/> Uptime: <b className="text-white" style={{ color: '#00f2ff' }}>{data.uptime}</b>
                 </div>
               </div>
             </header>
       
        {data.ai_tokens_spent >= data.ai_tokens_limit && (
          <div className="alert bg-danger bg-opacity-10 border-danger text-danger d-flex align-items-center gap-3 m-4 py-3 shadow-lg" 
              style={{ borderRadius: '12px', border: '1px solid #ef4444', animation: 'fadeIn 0.5s ease-in-out' }}>
            <ShieldAlert size={24} className="text-danger" />
            <div className="flex-grow-1">
              <div className="fw-bold" style={{ fontSize: '1rem' }}>ATTENTION! AI TOKENS EXPIRED</div>
              <div className="small opacity-80">
                Your current plan's AI analysis tokens have been fully consumed. 
                Automated threat detection is now paused. Please 
                <b className="text-decoration-underline ms-1 pointer" style={{cursor: 'pointer'}} onClick={() => navigate('/subscription')}>upgrade your plan</b> 
                to resume AI monitoring or analyze logs manually.
              </div>
            </div>
            <button className="btn btn-danger btn-sm fw-bold px-3" onClick={() => navigate('/subscription')}>
              UPGRADE NOW
            </button>
          </div>
        )}

        <div className="p-4" style={{ transition: '0.3s' }}>
          <div className="d-flex align-items-center gap-3 mb-1">
             <h2 className="fw-bold m-0 text-white">
              Dashboard {companyName && ` — ${companyName}`}
            </h2>
            </div>
          <p className="text-secondary small mb-4">Real-time status & AI Threat Analysis</p>

          <div className="row g-3 mb-4">
             <div className="col-md-4">
              <StatCard 
                label="Critical AI Incidents" 
                value={
                  allIncidents.filter(inc => 
                    inc.severity === 1 && 
                    (inc.status === 'new' || inc.status === 'assigned')
                  ).length
                } 
                color="#ef4444" 
                icon={<AlertTriangle size={18} />} 
              />
            </div>
            <div className="col-md-4"><StatCard label="Active Agents" value={data.agents_online} color="#10b981" icon={<Server size={18} />} /></div>
            <div className="col-md-4"><StatCard label="Data Rate" value={data.log_rate} color="#3b82f6" icon={<Activity size={18} />} /></div>
          </div>

          <div className="noc-card p-4 mb-4" style={{background: '#151921', borderRadius: '12px', border: '1px solid #1e293b'}}>
            <h6 className="m-0 mb-4 fw-bold text-secondary uppercase" style={{fontSize: '0.75rem', letterSpacing: '1px'}}>Incident Timeline (24h)</h6>
            <div style={{ width: '100%', height: 260 }}><ResponsiveContainer width="100%" height="100%"><AreaChart data={data.chart_data}><defs><linearGradient id="colorWave" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#00f2ff" stopOpacity={0.2}/><stop offset="95%" stopColor="#00f2ff" stopOpacity={0}/></linearGradient></defs><CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#1e293b" /><XAxis dataKey="time" stroke="#475569" fontSize={10} axisLine={false} tickLine={false} /><YAxis stroke="#475569" fontSize={10} axisLine={false} tickLine={false} /><Tooltip contentStyle={{background: '#0b0e14', border: '1px solid #1e293b'}}/><Area type="monotone" dataKey="ai" stroke="#00f2ff" strokeWidth={3} fill="url(#colorWave)" /></AreaChart></ResponsiveContainer></div>
          </div>

          <div className="noc-card p-0" style={{background: '#151921', borderRadius: '12px', border: '1px solid #1e293b'}}>
            <div className="p-3 border-bottom border-secondary d-flex justify-content-between align-items-center" style={{background: 'rgba(0,0,0,0.15)'}}>
               <div className="d-flex align-items-center gap-2"><ShieldAlert size={20} className="text-danger"/><h6 className="m-0 fw-bold">Recent AI Security Feed</h6></div>
               <span className="text-secondary small">Live Monitoring / Last 10 reports</span>
            </div>
           
            <table className="table-noc m-0 w-100">
              <thead style={{background: 'rgba(0,0,0,0.2)'}}>
                <tr className="text-secondary small text-uppercase">
                  <th className="p-3 border-0">ID</th>
                  <th className="p-3 border-0">Title / Created</th>
                  <th className="p-3 border-0">Status</th>
                  <th className="p-3 border-0">Assignee / Action</th>
                  <th className="p-3 border-0">Severity</th> 
                  <th className="p-3 border-0 text-end">Action</th>
                </tr>
              </thead>
              <tbody>
                {recentIncidents.map(inc => (
                  <tr 
                      key={inc.id} 
                      id={`inc-${inc.id}`} 
                      style={{
                        borderBottom: '1px solid #1e293b', 
                        transition: 'all 0.3s ease',
                        cursor: 'pointer',
                        backgroundColor: selectedInc?.id === inc.id ? 'rgba(0, 242, 255, 0.12)' : 'transparent',
                        borderLeft: selectedInc?.id === inc.id ? '4px solid #00f2ff' : '4px solid transparent'
                      }}
                      onClick={() => inspectIncident(inc)}
                    >
                    <td className="p-3 text-info font-monospace fw-bold">INC-{inc.display_id || inc.id}</td>
                    <td className="p-3">
                      <div className="fw-bold text-white small">{inc.title}</div>
                      <div className="text-secondary mt-1" style={{fontSize: '11px'}}><Clock size={10} className="me-1"/>{inc.created_at}</div>
                    </td>
                    <td className="p-3">
                      <span className="badge" style={{
                        fontSize: '10px', 
                        background: inc.status === 'resolved' ? 'rgba(16,185,129,0.1)' : inc.status === 'assigned' ? 'rgba(59,130,246,0.1)' : 'rgba(255, 74, 74, 0.1)', 
                        color: inc.status === 'resolved' ? '#10b981' : inc.status === 'assigned' ? '#3b82f6' : '#ff4a4a',
                        border: `1px solid ${inc.status === 'resolved' ? '#10b98144' : inc.status === 'assigned' ? '#3b82f644' : '#ff4a4a44'}`
                      }}>{(inc.status || 'new').toUpperCase()}</span>
                    </td>

                    <td className="p-3" onClick={(e) => e.stopPropagation()}>
                                          <div className="d-flex align-items-center gap-2">
                                            
                                            {userRole === 'Administrator' ? (
                                              
                                              <div 
                                                className="d-flex align-items-center bg-dark rounded border border-secondary px-2 py-1 transition-all hover-border-info" 
                                                style={{ minWidth: '180px', position: 'relative', border: '1px solid #334155' }}
                                              > 
                                                <UserCircle size={16} className="text-info position-absolute" style={{ left: '8px' }}/> 
                                                
                                                <select 
                                                  value={inc.assignee || ''} 
                                                  onChange={(e) => handleAssign(inc.id, e.target.value)} 
                                                  className="bg-transparent border-0 text-white small fw-bold w-100 custom-select-noc" 
                                                  style={{ 
                                                    outline: 'none', 
                                                    fontSize: '11px', 
                                                    paddingLeft: '28px', 
                                                    paddingRight: '20px',
                                                    cursor: 'pointer',
                                                    appearance: 'none', 
                                                    WebkitAppearance: 'none'
                                                  }}
                                                >
                                                  <option value="" style={{ background: '#0d1117', color: '#fff' }}>Unassigned</option>
                                                  {users
                                                    .filter(u => { 
                                                      const itemTid = Number(inc.tenant_id || 1); 
                                                      const userTid = Number(u.tenant_id);
                                                      if (itemTid === 1) return userTid === 1;
                                                      return userTid === itemTid || userTid === 1;
                                                    })
                                                    .map(u => (
                                                      <option key={u.id} value={u.username} style={{ background: '#0d1117', color: '#fff' }}>
                                                        {u.username} 
                                                      </option>
                                                    ))
                                                  }
                                                </select>
                                                <ChevronDown size={14} className="text-secondary position-absolute" style={{ right: '8px', pointerEvents: 'none' }} />
                                              </div>
                                            ) : (
                                              
                                              <div className="d-flex align-items-center gap-2">
                                                <span className="text-white small">
                                                  <UserCircle size={14} className="text-secondary me-1"/>
                                                  {inc.assignee || 'Unassigned'}
                                                </span>
                                                
                                                {!inc.assignee && (
                                                  <button 
                                                    onClick={(e) => { e.stopPropagation(); handleAssign(inc.id, currentUser); }}
                                                    className="btn btn-xs btn-outline-info py-0 px-2 fw-bold" 
                                                    style={{ fontSize: '9px', height: '22px', borderRadius: '4px' }}
                                                  >
                                                    PICK UP
                                                  </button>
                                                )}
                                              </div>
                                            )}
                    
                                          {inc.status === 'assigned' && inc.assignee === currentUser && (
                                            <button 
                                              onClick={(e) =>  { e.stopPropagation(); handleResolve(inc.id); }}
                                              className="btn btn-success btn-sm py-0 px-2 fw-bold shadow-success" 
                                              style={{ fontSize: '9px', height: '24px', borderRadius: '4px' }}
                                            >
                                              RESOLVE
                                            </button>
                                          )}
                                        </div>
                                      </td>

                      <td className="p-3">
                      <span className={`badge ${inc.severity === 1 ? 'badge-sev-1' : inc.severity === 2 ? 'badge-sev-2' : 'badge-sev-3'}`}>
                        {inc.severity === 1 ? 'CRITICAL' : inc.severity === 2 ? 'WARNING' : 'INFO'}
                      </span>
                    </td>
                    <td className="p-3 text-end">
                      <button onClick={() => inspectIncident(inc)} className="btn btn-sm btn-outline-info py-1 px-2 fw-bold" style={{fontSize: '11px'}}>
                        <Eye size={14} className="me-1"/> Inspect
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div style={{ 
          position: 'fixed', top: 0, right: selectedInc ? 0 : '-550px', width: '550px', 
          height: '100vh', background: '#0d1117', zIndex: 2000, borderLeft: '1px solid #1e293b', 
          overflow: 'hidden', transition: '0.3s' 
        }}>
          {selectedInc && (
            <div className="h-100 d-flex flex-column position-relative">
              

              <div className="flex-grow-1 overflow-auto p-4 d-flex flex-column text-start">
                <div className="d-flex justify-content-between align-items-center mb-4 pb-3 border-bottom border-secondary border-opacity-30">
                  <h4 className="m-0 fw-bold text-info d-flex align-items-center gap-2">
                      <BrainCircuit size={20}/> AI Analysis
                  </h4>
                  <X size={24} className="text-secondary cursor-pointer hover-text-white" onClick={() => setSelectedInc(null)} />
                </div>

                <div className="mb-3 d-flex gap-2">
                  <div className="badge bg-info bg-opacity-10 text-info border border-info border-opacity-25 px-2 py-1">
                    <Server size={10} className="me-1"/> {getOs(selectedInc.description)}
                  </div>
                  {selectedInc.service && (
                    <div className="badge bg-secondary bg-opacity-10 text-secondary border border-secondary border-opacity-25 px-2 py-1">
                      {selectedInc.service.toUpperCase()}
                    </div>
                  )}
                </div>

                <div className="mb-4">
                  <div className="small text-secondary text-uppercase fw-bold mb-2" style={{fontSize: '10px'}}>Technical Summary</div>
                  <div className="p-3 rounded bg-black bg-opacity-40 border-start border-info border-4 text-white small">
                    {getAnalysisContent(selectedInc)}
                  </div>
                </div>
                {console.log("RENDER ipIntelligence:", ipIntelligence)}
                
                                <div className="d-flex flex-column gap-4 mb-4">
                                  {ipIntelligence && ipIntelligence.length > 0 && ipIntelligence.map((ip, idx) => {
                                    const isDangerous = (ip.abuse_score || 0) > 40;
                                    const accentColor = isDangerous ? '#ef4444' : '#00f2ff';
                
                                    return (
                                      <div key={idx} className="p-0 rounded-3 border overflow-hidden animate-fade-in" 
                                          style={{ 
                                            borderColor: 'rgba(255, 255, 255, 0.1)',
                                            background: '#111827', 
                                            boxShadow: '0 10px 30px -10px rgba(0,0,0,0.5)'
                                          }}>
                
                                        <div className="p-3 d-flex justify-content-between align-items-center" 
                                            style={{ 
                                                background: 'rgba(0,0,0,0.2)', 
                                                borderBottom: '1px solid rgba(255,255,255,0.05)',
                                                borderLeft: `4px solid ${accentColor}` 
                                            }}>
                                          <div className="d-flex align-items-center gap-2">
                                            <Shield size={16} style={{ color: accentColor }} />
                                            <span className="fw-bold small uppercase" style={{ color: '#f8fafc', letterSpacing: '1px' }}>
                                              Intelligence Report: <span style={{ color: accentColor }}>{ip.ip}</span>
                                            </span>
                                          </div>
                
                                          <div className="px-2 py-1 rounded-pill" style={{ 
                                              fontSize: '9px', 
                                              fontWeight: '800', 
                                              border: `1px solid ${accentColor}66`, 
                                              color: accentColor,
                                              background: `${accentColor}11`
                                          }}>
                                            {isDangerous ? 'MALICIOUS' : 'SUSPICIOUS'}
                                          </div>
                                        </div>
                
                                        <div className="p-4 text-start">
                
                                          <div className="row g-4 mb-4">
                                            <div className="col-6">
                                              <div className="text-secondary mb-1" style={{ fontSize: '10px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase' }}>Provider / Organization</div>
                                              <div className="text-white fw-bold" style={{ fontSize: '14px' }}>{ip.isp || 'Unknown Provider'}</div>
                                              <div className="text-info x-small mt-1 opacity-80" style={{ fontSize: '11px', color: '#38bdf8' }}>{ip.org || ip.as}</div>
                                            </div>
                                            <div className="col-6 text-end">
                                              <div className="text-secondary mb-1" style={{ fontSize: '10px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase' }}>Geolocation</div>
                                              <div className="text-white fw-bold">
                                                {ip.city}, {ip.country} 
                                                <span className="ms-2">{ip.countryCode === 'UA' ? '🇺🇦' : ip.countryCode === 'GB' ? '🇬🇧' : '🌐'}</span>
                                              </div>
                                              <div className="text-secondary x-small mt-1" style={{ color: '#475569' }}>Registered Network Location</div>
                                            </div>
                                          </div>
                
                                          <div className="mb-4">
                                            <div className="text-secondary mb-2" style={{ fontSize: '10px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase' }}>Infrastructure Flags</div>
                                            <div className="d-flex flex-wrap gap-2">
                                              <div className="d-flex align-items-center gap-2 px-3 py-2 rounded border" 
                                                  style={{ 
                                                    fontSize: '11px', 
                                                    borderColor: ip.is_hosting ? '#ef444444' : '#334155',
                                                    background: ip.is_hosting ? '#ef444411' : 'transparent',
                                                    color: ip.is_hosting ? '#f87171' : '#94a3b8'
                                                  }}>
                                                <Server size={14}/> {ip.is_hosting ? 'Datacenter / Botnet' : 'Residential IP'}
                                              </div>
                                              
                                              <div className="d-flex align-items-center gap-2 px-3 py-2 rounded border" 
                                                  style={{ 
                                                    fontSize: '11px', 
                                                    borderColor: ip.is_proxy ? '#f59e0b44' : '#334155',
                                                    background: ip.is_proxy ? '#f59e0b11' : 'transparent',
                                                    color: ip.is_proxy ? '#fbbf24' : '#94a3b8'
                                                  }}>
                                                <Activity size={14}/> Proxy/VPN: {ip.is_proxy ? 'Detected' : 'Not Active'}
                                              </div>
                                            </div>
                                          </div>
                
                                          <div className="mb-4 p-3 rounded" style={{ background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.05)' }}>
                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                              <span className="fw-bold" style={{ fontSize: '10px', color: '#94a3b8' }}>ABUSE CONFIDENCE SCORE</span>
                                              <span className="fw-bold" style={{ fontSize: '14px', color: accentColor }}>{ip.abuse_score || 0}%</span>
                                            </div>
                                            <div className="progress" style={{ height: '6px', background: 'rgba(255,255,255,0.05)', borderRadius: '10px' }}>
                                              <div className="progress-bar" 
                                                  style={{ 
                                                    width: `${ip.abuse_score || 0}%`, 
                                                    backgroundColor: accentColor,
                                                    boxShadow: `0 0 10px ${accentColor}66`,
                                                    transition: 'width 1.5s ease', 
                                                    borderRadius: '10px' 
                                                  }}></div>
                                            </div>
                                          </div>
                
                                          {ip.ai_report && (
                                            <div className="mt-4 pt-3 border-top border-secondary border-opacity-20">
                                              <div className="d-flex align-items-center gap-2 mb-3">
                                                <BrainCircuit size={16} className="text-info" />
                                                <span className="text-info fw-bold" style={{ fontSize: '10px', letterSpacing: '1px', textTransform: 'uppercase' }}>AI Security Verdict</span>
                                              </div>
                                              <div className="p-3 rounded border-start border-info border-4" style={{ background: 'rgba(0, 242, 255, 0.03)' }}>
                                                <p className="text-white m-0" style={{ fontSize: '12.5px', lineHeight: '1.7', opacity: 0.9 }}>
                                                  {ip.ai_report}
                                                </p>
                                              </div>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>

                {renderRecommendation(selectedInc.recommendation)}

              
                <div className="mb-5">
                  <button 
                    onClick={() => {
                      setIsChatOpen(true);
                      setIsChatView('list'); 
                      fetchIncidentChats(selectedInc.id); 
                    }}
                    className="btn btn-outline-info w-100 fw-bold py-3 d-flex align-items-center justify-content-center gap-2 transition-all"
                    style={{ 
                      borderStyle: 'dashed', 
                      borderWidth: '2px', 
                      background: 'rgba(0, 242, 255, 0.03)',
                      color: '#00f2ff', 
                      borderColor: 'rgba(0, 242, 255, 0.5)'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(0, 242, 255, 0.1)';
                      e.currentTarget.style.color = '#fff'; 
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'rgba(0, 242, 255, 0.03)';
                      e.currentTarget.style.color = '#00f2ff';
                    }}
                  >
                    <MessageSquare size={18} /> 
                    <span>Open chat for help</span>
                  </button>
                </div>

                <div className="mt-auto pt-4 border-top border-secondary border-opacity-30">
                  <div className="small text-secondary text-uppercase fw-bold mb-3" style={{fontSize: '10px'}}>Affected Raw Logs ({relatedLogs.length})</div>
                  <div className="bg-black bg-opacity-40 rounded border border-secondary border-opacity-20 p-1 font-monospace" style={{maxHeight: '300px', overflowY: 'auto', fontSize: '11px'}}>
                    {relatedLogs.map(log => (
                      <div key={log.id} className="mb-1 border-bottom border-secondary border-opacity-10">
                        <div onClick={() => setExpandedLogId(expandedLogId === log.id ? null : log.id)} className="p-2 d-flex justify-content-between cursor-pointer hover-bg-white-faded">
                          <div className="d-flex align-items-center gap-2">
                            <ChevronDown size={14} className="text-secondary" style={{ transform: expandedLogId === log.id ? 'rotate(0deg)' : 'rotate(-90deg)', transition: '0.3s' }} />
                            <span className="text-info fw-bold">[RAW-{log.display_id || log.id}]</span>
                            <span className="text-white-50">{log.host}</span>
                          </div>
                        </div>
                        {expandedLogId === log.id && (
                          <div className="p-3 bg-black border-top border-secondary border-opacity-10 text-start">
                             <div className="d-flex justify-content-between align-items-center mb-2">
                                <div className="text-info small">{log.title}</div>
                                <button onClick={() => navigate('/logs', { state: { autoOpenId: log.display_id || log.id } })} className="btn btn-xs btn-outline-info py-0 px-2" style={{fontSize: '9px'}}>View Context</button>
                             </div>
                             <pre className="m-0" style={{ color: '#7ee787', fontSize: '10px', background: '#010409', padding: '10px', border: '1px solid #1e293b' }}>
                               {log.evidence ? JSON.stringify(log.evidence, null, 2) : "// No detailed data"}
                             </pre>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              
              {isChatOpen && (
                <div className="position-absolute top-0 start-0 w-100 h-100 d-flex flex-column shadow-lg" style={{ background: '#0d1117', zIndex: 3000 }}>
                  
                  <div className="p-3 border-bottom border-secondary border-opacity-40 d-flex justify-content-between align-items-center bg-black bg-opacity-50">
                    <div className="d-flex align-items-center gap-2 text-info fw-bold small">
                      <BrainCircuit size={18} className="text-info shadow-info" />
                      {isChatView === 'list' ? 'INVESTIGATION SESSIONS' : 'AI INCIDENT ASSISTANT'}
                    </div>
                    <div className="d-flex gap-2">
                      {isChatView === 'messages' && (
                        <button onClick={() => setIsChatView('list')} className="btn btn-xs btn-outline-info py-0 px-2 fw-bold" style={{fontSize: '9px', height: '24px'}}>BACK TO LIST</button>
                      )}
                      <X size={20} className="text-secondary cursor-pointer hover-text-danger" onClick={() => setIsChatOpen(false)} />
                    </div>
                  </div>

                  <div className="flex-grow-1 overflow-hidden d-flex flex-column">
                    {isChatView === 'list' ? (
                      <div className="p-4 overflow-auto h-100 d-flex flex-column">
                        
                        
                        {Number(tenantId) === Number(selectedInc.tenant_id) && (
                        <button 
                          onClick={handleCreateNewChat} 
                          className="btn btn-info w-100 fw-bold mb-4 d-flex align-items-center justify-content-center gap-2 py-3 shadow-info"
                          disabled={isTokensExpired} 
                          style={{ opacity: isTokensExpired ? 0.5 : 1 }}
                        >
                          <Zap size={18}/> {isTokensExpired ? 'LIMIT EXPIRED' : 'START NEW INVESTIGATION'}
                        </button>
                      )}

                      {isTokensExpired && (
                            <div className="p-2 mb-3 rounded border border-danger bg-danger bg-opacity-10 text-danger small text-center fw-bold">
                                AI features are locked. Please upgrade.
                            </div>
                        )}
                                            
                        <div className="small text-secondary text-uppercase fw-bold mb-3 border-bottom border-secondary border-opacity-20 pb-2" style={{fontSize: '10px'}}>Session History</div>
                        
                        <div className="d-flex flex-column gap-2">
                          {availableChats.length === 0 ? (
                            <div className="text-center text-white-50 mt-5 small italic opacity-50">No sessions found.</div>
                          ) : (
                            availableChats.map(chat => {
                              const isOwner = Number(tenantId) === Number(chat.owner_id);
                              const isActive = activeChatId === chat.id;

                              return (
                                <div 
                                  key={chat.id}
                                  onClick={() => loadChatSession(chat.id, chat.owner_id)} 
                                  className={`p-3 rounded mb-2 border cursor-pointer transition-all ${
                                      isActive ? 'border-info bg-info bg-opacity-10' : 'border-secondary border-opacity-20 bg-secondary bg-opacity-5 hover-bg-opacity-10'
                                  }`}
                                  style={{ 
                                      borderLeft: isActive ? '4px solid #00f2ff' : '1px solid rgba(255,255,255,0.1)',
                                      position: 'relative'
                                  }}
                                >
                                  <div className="d-flex justify-content-between align-items-center">
                                      <div style={{ maxWidth: '85%' }}>
                                          <div className="text-white small fw-bold text-truncate">{chat.title}</div>
                                          <div className="text-secondary d-flex align-items-center gap-1" style={{fontSize: '9px'}}>
                                            <Clock size={10} /> {chat.date}
                                          </div>
                                      </div>
                                    
                                     
                                      {isOwner && (
                                        <button 
                                          onClick={(e) => {
                                            e.stopPropagation(); 
                                            handleDeleteChat(e, chat.id);
                                          }} 
                                          className="btn btn-link p-1 transition-all"
                                          style={{ color: '#94a3b8' }}
                                          onMouseEnter={(e) => e.currentTarget.style.color = '#ff4a4a'}
                                          onMouseLeave={(e) => e.currentTarget.style.color = '#94a3b8'}
                                          title="Delete session"
                                        >
                                          <Trash2 size={16} />
                                        </button>
                                      )}
                                  </div>
                                </div>
                              );
                            })
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="d-flex flex-column h-100">
                        <div id="chat-scroll" className="flex-grow-1 p-3 overflow-auto d-flex flex-column gap-3 bg-black bg-opacity-10">
                          {chatMessages.map((msg, idx) => (
                            <div key={idx} className={`p-2 rounded-3 max-w-90 ${msg.role === 'user' ? 'bg-info bg-opacity-10 align-self-end border border-info border-opacity-20 ms-5' : 'bg-secondary bg-opacity-20 align-self-start border border-secondary border-opacity-20 me-5'}`}>
                               <div className="text-white small" style={{ fontSize: '12px', whiteSpace: 'pre-wrap', fontFamily: msg.role === 'assistant' ? "'Fira Code', monospace" : 'inherit', lineHeight: '1.6' }}>
                                 {msg.content}
                               </div>
                            </div>
                          ))}
                          {isChatLoading && (
                            <div className="d-flex align-items-center gap-2 text-info small p-2"><Zap size={14} className="animate-spin" /> AI Analyzing...</div>
                          )}
                        </div>

                       
                         <div className="p-3 border-top border-secondary bg-black bg-opacity-40">
                          <div className="input-group">
                            <input 
                              autoFocus 
                              value={chatInput} 
                              onChange={(e) => setChatInput(e.target.value)} 
                              onKeyDown={(e) => e.key === 'Enter' && sendChatMessage()} 
                              className="form-control bg-dark border-secondary text-white shadow-none" 
                              style={{ fontSize: '13px' }}
                              
                              
                              disabled={isChatLoading || Number(tenantId) !== Number(activeChatOwnerId) || isTokensExpired} 
                              placeholder={
                                isTokensExpired ? "AI Tokens limit reached" :
                                Number(tenantId) !== Number(activeChatOwnerId) ? "Read-only mode" : 
                                "Ask a technical question..."
                              }
                            />
                            {Number(tenantId) === Number(activeChatOwnerId) && !isTokensExpired && (
                              <button onClick={sendChatMessage} className="btn btn-info px-3" disabled={isChatLoading}>
                                <Zap size={16} />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </SubscriptionGuard>
    </div>
  );
};


export default Dashboard;